import ListFilters from "@/common/components/ListFilters";
import Modal from "@/common/components/Modal";
import TextField from "@/common/components/TextField";
import useDebounce from "@/common/hooks/useDebounce";
import { Mapper } from "@/common/services/mapper";
import { CompanyBranchData } from "@/common/types/companyBranches.type";
import { Option } from "@/common/types/option";
import { SearchIcon } from "lucide-react";
import { useState } from "react";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import { SelectEquipmentGridTable } from "./SelectEquipmentGridTable";
import Button from "@/common/components/Button";
import { useEquipments } from "@/features/equipments/hooks/useEquipments";
import { EquipmentCategoryData, EquipmentData } from "@/features/equipments/types/equipments.type";
import { useEquipmentCategories } from "@/features/equipments/hooks/useEquipmentCategories";
import { useAvailableCompanyBranches } from "@/features/companyBranches/hooks/useAvailableCompanyBranches";
import { equipmentStatusTranslation } from "@/common/constants/equipmentStatus.constants";
import { options as propertyKindOptions } from "@/features/equipments/constants/propertyKindOptions.constant";
import { options as usageKindOptions } from "@/features/equipments/constants/usageKindOptions.constant";

const SelectEquipmentModal = () => {
  const [filters, setFilters] = useState({
    search: '',
    page: 1 as number,
    category: null as Option | null,
    status: { label: 'Ativo', value: 'enabled' } as Option | null,
    usageKind: null as Option | null,
    propertyKind: null as Option | null,
    branch: null as Option | null,
  });

  const [search, setSearch] = useDebounce('');

  const [searchCategory, setSearchCategory] = useDebounce('');
  const [searchBranch, setSearchBranch] = useDebounce('');

  const {
    data: companyEquipments,
    meta,
    isPending: isPendingCompanyEquipments,
    error: companyEquipmentsError = {} as any,
    refetch: refetchCompanyEquipments,
  } = useEquipments<EquipmentData[]>({
    filters: {
      search: search,
      page: filters.page || 1,
      categoryId: filters.category?.value,
      status: filters.status?.value,
      usageKind: filters.usageKind?.value,
      propertyKind: filters.propertyKind?.value,
      branchId: filters.branch?.value,
    },
  })

  const {
    data: equipmentCategoryOptions,
  } = useEquipmentCategories({
    filters: {
      search: searchCategory,
    },
    mapper: Mapper.mapToOptions<EquipmentCategoryData>({
      labelFieldName: 'display_name',
      valueFieldName: 'id',
    }),
  })

  const {
    data: companyBranchOptions,
  } = useAvailableCompanyBranches({
    filters: { search: searchBranch },
    mapper: Mapper.mapToOptions<CompanyBranchData>({
      labelFieldName: 'display_name',
      valueFieldName: 'id',
    }),
  })

  return (
    <Modal
      title="Selecione um equipamento"
      Trigger={(
        <Button operationKlassToCheckPermission="Client::Maintenance::Workflows::Create">
          Criação rápida
        </Button>
      )}
      className="max-w-screen-lg w-screen"
      body={(
        <ErrorMessageWrapper
          statusCode={companyEquipmentsError?.status}
          refetchFunction={refetchCompanyEquipments}
        >
          <div className="flex flex-col gap-6 text-white-800">
            <div className="flex flex-col gap-4">
              <TextField
                LeftIcon={<SearchIcon />}
                placeholder="Pesquisar"
                className="rounded-sm shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.5)]"
                onChange={(e) => {
                  setSearch((e.target as HTMLInputElement).value);
                  setFilters((prev) => ({ ...prev, search: (e.target as HTMLInputElement).value }))
                }}
                value={filters.search}
              />

              <ListFilters
                filters={[
                  {
                    className: 'bg-white-200',
                    placeholder: 'Tipo de equipamento',
                    options: [{ label: 'Todos', value: '' }, ...(equipmentCategoryOptions || [])],
                    onChange: (value) => setFilters((prev) => ({ ...prev, category: value as Option })),
                    onInputChange: setSearchCategory,
                    value: filters.category,
                  },
                  {
                    className: 'bg-white-200',
                    placeholder: 'Status',
                    options: [
                      { label: 'Todos', value: '' },
                      ...Object.entries(equipmentStatusTranslation).map(([key, value]) => ({
                        label: value,
                        value: key
                      }))
                    ],
                    onChange: (value) => setFilters((prev) => ({ ...prev, status: value as Option })),
                    value: filters.status,
                  },
                  {
                    className: 'bg-white-200',
                    placeholder: 'Filial',
                    options: [
                      { label: 'Todos', value: '' },
                      ...companyBranchOptions,
                    ],
                    onChange: (value) => setFilters((prev) => ({ ...prev, branch: value as Option })),
                    onInputChange: setSearchBranch,
                    value: filters.branch,
                  },
                  {
                    className: 'bg-white-200',
                    placeholder: 'Tipo de uso',
                    options: [{ label: 'Todos', value: '' }, ...(usageKindOptions || [])],
                    onChange: (value) => setFilters((prev) => ({ ...prev, usageKind: value as Option })),
                    value: filters.usageKind,
                  },
                  {
                    className: 'bg-white-200',
                    placeholder: 'Tipo de propriedade',
                    options: [{ label: 'Todos', value: '' }, ...(propertyKindOptions || [])],
                    onChange: (value) => setFilters((prev) => ({ ...prev, propertyKind: value as Option })),
                    value: filters.propertyKind,
                  },
                ]}
              />
            </div>

            <SelectEquipmentGridTable
              className="max-h-[calc(100vh-356px)]"
              companyEquipments={companyEquipments || []}
              page={filters.page || 1}
              totalPages={meta.total_pages || 1}
              totalCount={meta.total}
              setPage={(page) => setFilters((prev) => ({ ...prev, page: (page || 1) as number }))}
              isLoadingData={isPendingCompanyEquipments}
            />
          </div>
        </ErrorMessageWrapper>
      )}
    />
  );
}

export default SelectEquipmentModal;
